import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { WatchlistService } from "../services/watchlist-service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import type { Watchlist } from "@/schemas";
import {
  renameWatchlistFormSchema,
  type RenameWatchlistFormData,
} from "@/schemas/user.schema";

interface RenameWatchlistDialogProps {
  watchlist: Watchlist | null;
  isOpen: boolean;
  onClose: () => void;
  onRenamed: (updated: Watchlist) => void;
}

export function RenameWatchlistDialog({
  watchlist,
  isOpen,
  onClose,
  onRenamed,
}: RenameWatchlistDialogProps) {
  const form = useForm<RenameWatchlistFormData>({
    resolver: zodResolver(renameWatchlistFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const { isSubmitting } = form.formState;

  // Reset form when dialog opens with a new watchlist
  useEffect(() => {
    if (isOpen && watchlist) {
      form.reset({ name: watchlist.name });
    }
  }, [isOpen, watchlist, form]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const onSubmit = async (data: RenameWatchlistFormData) => {
    if (!watchlist) return;

    try {
      const updated = await WatchlistService.rename(
        watchlist.watchlist_id,
        data.name
      );
      onRenamed(updated);
      toast.success("Watchlist renamed");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to rename watchlist");
    }
  };

  const isUnchanged = form.watch("name").trim() === watchlist?.name;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Watchlist</DialogTitle>
          <DialogDescription>
            Enter a new name for your watchlist.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="py-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Watchlist name"
                        {...field}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            form.handleSubmit(onSubmit)();
                          }
                        }}
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isUnchanged}>
                {isSubmitting && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Rename
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
